import { useEffect, useMemo, useState } from 'react';
import { Lightning, X } from '@phosphor-icons/react';
import type { GameState } from '../game/types';
import { useI18n } from '../i18n';

interface Props { game: GameState; onOpenResearch?: () => void }

export function EffectBanner({ game, onOpenResearch }: Props) {
  const [dismissed, setDismissed] = useState(false);
  const { t } = useI18n();

  const effects = game.activeEffects ?? [];
  const marketEvents = game.activeMarketEvents ?? [];
  const vouchers = game.vouchers ?? {};
  const research = game.researchOpportunity;
  const activeKey = useMemo(() => [
    ...marketEvents.map((event) => `${event.id}:${event.weeksRemaining}`),
    ...effects.map((effect) => `${effect.id}:${effect.weeksRemaining}`),
    vouchers.hubDiscount70 ? 'hub' : '',
    vouchers.aircraftDiscount70 ? 'aircraft' : '',
    research ? `research:${research.status}` : '',
  ].join('|'), [effects, marketEvents, research, vouchers.aircraftDiscount70, vouchers.hubDiscount70]);
  const hasActive = effects.length > 0 || marketEvents.length > 0 || vouchers.hubDiscount70 || vouchers.aircraftDiscount70 || !!research;

  useEffect(() => {
    setDismissed(false);
  }, [activeKey]);
  if (!hasActive || dismissed) return null;

  return (
    <div className="effect-banner" role="alert" aria-label={t('ui.周期奖惩生效中')}>
      <div className="effect-banner-content">
        <span className="effect-banner-icon" aria-hidden="true">
          <Lightning weight="fill" size={20} />
        </span>
        <div>
          <strong>{t('ui.市场窗口进行中')}</strong>
          <div className="effect-banner-list">
            {marketEvents.map((event) => (
              <span key={event.id} className={event.tone === 'positive' ? 'green' : event.tone === 'negative' ? 'red' : ''} title={event.response}>
                {event.targetLabel} · {event.title} · {t('ui.剩余 {weeks} 周', { weeks: event.weeksRemaining })}
              </span>
            ))}
            {effects.map((e) => (
              <span key={e.id} className={e.kind === 'fuelCostModifier' || e.multiplier < 1 ? 'red' : 'green'}>
                {t(e.kind === 'fuelCostModifier' ? 'ui.燃油成本' : 'ui.营收')} {e.multiplier > 1 ? '+' : '-'}{Math.round(Math.abs(e.multiplier - 1) * 100)}% · {t('ui.剩余 {weeks} 周', { weeks: e.weeksRemaining })}
              </span>
            ))}
            {vouchers.hubDiscount70 && <span className="green">{t('ui.7 折枢纽券待用')}</span>}
            {vouchers.aircraftDiscount70 && <span className="green">{t('ui.7 折飞机券待用')}</span>}
            {research && (
              <button className="effect-banner-research" onClick={onOpenResearch}>
                {t('ui.城市调研')}{research.status === 'available' ? t('ui.可用') : t('ui.待处理')} · {t('ui.第 {week} 周前', { week: research.expiresWeek })}
              </button>
            )}
          </div>
        </div>
      </div>
      <button className="effect-banner-close" onClick={() => setDismissed(true)} aria-label={t('ui.关闭')}><X weight="bold" size={16} /></button>
    </div>
  );
}
